import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { m, AnimatePresence } from 'framer-motion'
import { X, ExternalLink } from 'lucide-react'
import { trackEvent } from '../../../utils/analytics'

interface VideoModalProps {
    isOpen: boolean
    onClose: () => void
    videoUrl: string
    videoTitle: string
}

const VideoModal: React.FC<VideoModalProps> = ({ isOpen, onClose, videoUrl, videoTitle }) => {
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = 'unset'
        }
        return () => {
            document.body.style.overflow = 'unset'
        }
    }, [isOpen])

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose()
            }
        }
        if (isOpen) {
            window.addEventListener('keydown', handleEscape)
        }
        return () => window.removeEventListener('keydown', handleEscape)
    }, [isOpen, onClose])

    const handleClose = (e: React.MouseEvent) => {
        e.stopPropagation()
        onClose()
    }

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose()
        }
    }

    const handleOpenInNewTab = () => {
        trackEvent('video_open_external', { item_name: videoTitle })
        window.open(videoUrl, '_blank', 'noopener,noreferrer')
    }

    if (!isOpen || !videoUrl) return null

    // 네이버 플레이스 영상 URL을 iframe에 적합한 형태로 변환 시도
    const getEmbedUrl = (url: string): string | null => {
        // 네이버 쇼츠의 경우
        if (url.includes('m.naver.com/shorts/')) {
            // 네이버 쇼츠는 직접 임베드가 어려우므로 원본 URL 반환
            return null
        }
        // 네이버 플레이스 영상의 경우
        if (url.includes('m.place.naver.com')) {
            // 네이버 플레이스 영상도 직접 임베드가 어려울 수 있음
            return null
        }
        return url
    }

    const embedUrl = getEmbedUrl(videoUrl)
    const canEmbed = embedUrl !== null

    const modalContent = (
        <AnimatePresence>
            <m.div
                key="video-modal-backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={handleBackdropClick}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    width: '100vw',
                    height: '100vh',
                    backgroundColor: 'rgba(0, 0, 0, 0.95)',
                    backdropFilter: 'blur(8px)',
                    zIndex: 999999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: 0,
                    padding: 0,
                }}
            >
                {/* Close Button */}
                <m.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={handleClose}
                    style={{
                        position: 'fixed',
                        top: '1rem',
                        right: '1rem',
                        padding: '0.75rem',
                        backgroundColor: '#dc2626',
                        borderRadius: '0.5rem',
                        color: 'white',
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                        zIndex: 1000000,
                    }}
                    aria-label="Close"
                >
                    <X size={24} />
                </m.button>

                {/* Video Container */}
                <m.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        position: 'relative',
                        width: '90vw',
                        maxWidth: '1200px',
                        height: '80vh',
                        maxHeight: '800px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '1rem',
                    }}
                >
                    {canEmbed ? (
                        <iframe
                            src={embedUrl}
                            style={{
                                width: '100%',
                                height: '100%',
                                border: 'none',
                                borderRadius: '0.5rem',
                            }}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            title={videoTitle}
                        />
                    ) : (
                        <div
                            style={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '2rem',
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                borderRadius: '0.5rem',
                                padding: '2rem',
                            }}
                        >
                            <div
                                style={{
                                    textAlign: 'center',
                                    color: 'white',
                                }}
                            >
                                <h3
                                    style={{
                                        fontSize: '1.5rem',
                                        fontWeight: '600',
                                        marginBottom: '1rem',
                                    }}
                                >
                                    {videoTitle}
                                </h3>
                                <p
                                    style={{
                                        fontSize: '1rem',
                                        color: 'rgba(255, 255, 255, 0.7)',
                                        marginBottom: '2rem',
                                    }}
                                >
                                    영상을 보려면 아래 버튼을 클릭하세요
                                </p>
                                <m.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={handleOpenInNewTab}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        padding: '1rem 2rem',
                                        backgroundColor: '#dc2626',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '0.5rem',
                                        fontSize: '1rem',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        transition: 'background-color 0.2s',
                                    }}
                                >
                                    <ExternalLink size={20} />
                                    새 창에서 영상 보기
                                </m.button>
                            </div>
                        </div>
                    )}

                    {/* Caption */}
                    {videoTitle && (
                        <div
                            style={{
                                position: 'absolute',
                                bottom: '-3rem',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                backdropFilter: 'blur(12px)',
                                padding: '0.75rem 1.5rem',
                                borderRadius: '0.5rem',
                                color: 'white',
                                maxWidth: '90vw',
                                textAlign: 'center',
                            }}
                        >
                            {videoTitle}
                        </div>
                    )}
                </m.div>
            </m.div>
        </AnimatePresence>
    )

    return createPortal(modalContent, document.body)
}

export default VideoModal


